/**
 * CampaignPopupsPage - Admin page for managing in-app campaign popups
 * 
 * Features:
 * - Summary metrics (total, active, impressions, CTR)
 * - Table of all campaigns with stats
 * - Create/edit dialog with form
 * - Toggle activation
 * - Delete with confirmation
 */

import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api-client';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import {
    Megaphone, Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Eye,
    MousePointerClick, XCircle, Users, Calendar, Smartphone, Globe,
    Image as ImageIcon, ExternalLink, ArrowUpDown, Sparkles, Gift,
    Info, AlertTriangle, PartyPopper, Upload, Search, Link, Fish, X,
} from 'lucide-react';

// CDN base URL for resolving relative blob paths to full image URLs
const CDN_BASE_URL = 'https://aipeces-media-a4hsbvf5gff3b6b0.z02.azurefd.net';

// ─── Types ──────────────────────────────────────────────────────────────────

interface CampaignPopupStats {
    impressions: number;
    cta_clicks: number;
    dismissals: number;
    unique_users: number;
}

interface CampaignPopup {
    id: string;
    title: string;
    subtitle: string | null;
    body: string | null;
    image_url: string | null;
    cta_text: string;
    cta_url: string | null;
    dismiss_text: string;
    theme: 'default' | 'promo' | 'info' | 'warning' | 'celebration';
    target_platform: 'all' | 'android' | 'ios';
    is_active: boolean;
    start_date: string | null;
    end_date: string | null;
    priority: number;
    max_impressions_per_user: number;
    created_by: string | null;
    created_at: string;
    updated_at: string;
    stats: CampaignPopupStats;
}

interface CampaignForm {
    title: string;
    subtitle: string;
    body: string;
    image_url: string;
    cta_text: string;
    cta_url: string;
    dismiss_text: string;
    theme: CampaignPopup['theme'];
    target_platform: CampaignPopup['target_platform'];
    is_active: boolean;
    start_date: string;
    end_date: string;
    priority: number;
    max_impressions_per_user: number;
}

const EMPTY_FORM: CampaignForm = {
    title: '',
    subtitle: '',
    body: '',
    image_url: '',
    cta_text: 'Ver más',
    cta_url: '',
    dismiss_text: 'Cerrar',
    theme: 'default',
    target_platform: 'all',
    is_active: false,
    start_date: '',
    end_date: '',
    priority: 0,
    max_impressions_per_user: 1,
};

const THEME_OPTIONS: { value: CampaignPopup['theme']; label: string; icon: React.ReactNode; color: string }[] = [
    { value: 'default', label: 'Default', icon: <Megaphone size={14} />, color: 'bg-blue-500/20 text-blue-400' },
    { value: 'promo', label: 'Promo', icon: <Gift size={14} />, color: 'bg-amber-500/20 text-amber-400' },
    { value: 'info', label: 'Info', icon: <Info size={14} />, color: 'bg-cyan-500/20 text-cyan-400' },
    { value: 'warning', label: 'Warning', icon: <AlertTriangle size={14} />, color: 'bg-red-500/20 text-red-400' },
    { value: 'celebration', label: 'Celebration', icon: <PartyPopper size={14} />, color: 'bg-violet-500/20 text-violet-400' },
];

const PLATFORM_OPTIONS: { value: CampaignPopup['target_platform']; label: string; icon: React.ReactNode }[] = [
    { value: 'all', label: 'Todas', icon: <Globe size={14} /> },
    { value: 'android', label: 'Android', icon: <Smartphone size={14} /> },
    { value: 'ios', label: 'iOS', icon: <Smartphone size={14} /> },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

function getStatusInfo(campaign: CampaignPopup): { label: string; class: string } {
    if (!campaign.is_active) return { label: 'Inactiva', class: 'bg-gray-500/20 text-gray-400' };

    const now = new Date();
    if (campaign.start_date && new Date(campaign.start_date) > now) {
        return { label: 'Programada', class: 'bg-yellow-500/20 text-yellow-400' };
    }
    if (campaign.end_date && new Date(campaign.end_date) < now) {
        return { label: 'Expirada', class: 'bg-red-500/20 text-red-400' };
    }
    return { label: 'Activa', class: 'bg-emerald-500/20 text-emerald-400' };
}

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('es-ES', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function toLocalDatetime(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const offset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - offset).toISOString().slice(0, 16);
}

// ─── Component ──────────────────────────────────────────────────────────────

type ImagePickerTab = 'search' | 'upload' | 'url';

export function CampaignPopupsPage() {
    const queryClient = useQueryClient();
    const [editingCampaign, setEditingCampaign] = useState<CampaignPopup | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [form, setForm] = useState<CampaignForm>({ ...EMPTY_FORM });
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [imagePickerTab, setImagePickerTab] = useState<ImagePickerTab>('search');
    const [captureSearch, setCaptureSearch] = useState('');
    const [manualUrl, setManualUrl] = useState('');
    const [uploadAlert, setUploadAlert] = useState<string | null>(null);

    // ─── Data Fetching ──────────────────────────────────────────────────────

    const { data: campaigns = [], isLoading } = useQuery<CampaignPopup[]>({
        queryKey: ['campaign-popups'],
        queryFn: async () => {
            const result = await apiClient.rpc<CampaignPopup[]>('admin_list_campaign_popups');
            if (result.error) throw new Error(result.error.message);
            return result.data || [];
        },
    });

    // ─── Mutations ──────────────────────────────────────────────────────────

    const upsertMutation = useMutation({
        mutationFn: async (data: { id?: string; form: CampaignForm }) => {
            const params: Record<string, unknown> = {
                p_title: data.form.title,
                p_subtitle: data.form.subtitle || null,
                p_body: data.form.body || null,
                p_image_url: data.form.image_url || null,
                p_cta_text: data.form.cta_text || 'Ver más',
                p_cta_url: data.form.cta_url || null,
                p_dismiss_text: data.form.dismiss_text || 'Cerrar',
                p_theme: data.form.theme,
                p_target_platform: data.form.target_platform,
                p_is_active: data.form.is_active,
                p_start_date: data.form.start_date || null,
                p_end_date: data.form.end_date || null,
                p_priority: data.form.priority,
                p_max_impressions_per_user: data.form.max_impressions_per_user,
            };
            if (data.id) params.p_id = data.id;

            const result = await apiClient.rpc('admin_upsert_campaign_popup', params);
            if (result.error) throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['campaign-popups'] });
            setIsDialogOpen(false);
            setEditingCampaign(null);
            setForm({ ...EMPTY_FORM });
        },
    });

    const toggleMutation = useMutation({
        mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
            const result = await apiClient.rpc('admin_toggle_campaign_popup', { p_id: id, p_is_active: is_active });
            if (result.error) throw new Error(result.error.message);
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['campaign-popups'] }),
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const result = await apiClient.rpc('admin_delete_campaign_popup', { p_id: id });
            if (result.error) throw new Error(result.error.message);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['campaign-popups'] });
            setDeleteConfirmId(null);
        },
    });

    // ─── Computed stats ─────────────────────────────────────────────────────

    const stats = useMemo(() => {
        const total = campaigns.length;
        const active = campaigns.filter(c => c.is_active).length;
        const totalImpressions = campaigns.reduce((sum, c) => sum + (c.stats?.impressions || 0), 0);
        const totalClicks = campaigns.reduce((sum, c) => sum + (c.stats?.cta_clicks || 0), 0);
        const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(1) : '0.0';
        return { total, active, totalImpressions, totalClicks, ctr };
    }, [campaigns]);

    // ─── Image Upload ───────────────────────────────────────────────────────

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const fileName = `campaign-${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
            const result = await apiClient.uploadFile('campaign-images', fileName, file);
            if (result.error) {
                setUploadAlert('Error al subir imagen: ' + result.error.message);
            } else if (result.data) {
                setForm(prev => ({ ...prev, image_url: result.data!.publicUrl || result.data!.url }));
            }
        } catch {
            setUploadAlert('Error al subir imagen');
        } finally {
            setIsUploading(false);
        }
    };

    // ─── Dialog Handlers ───────────────────────────────────────────────────

    const openCreateDialog = () => {
        setEditingCampaign(null);
        setForm({ ...EMPTY_FORM });
        setIsDialogOpen(true);
    };

    const openEditDialog = (campaign: CampaignPopup) => {
        setEditingCampaign(campaign);
        setForm({
            title: campaign.title,
            subtitle: campaign.subtitle || '',
            body: campaign.body || '',
            image_url: campaign.image_url || '',
            cta_text: campaign.cta_text || 'Ver más',
            cta_url: campaign.cta_url || '',
            dismiss_text: campaign.dismiss_text || 'Cerrar',
            theme: campaign.theme,
            target_platform: campaign.target_platform,
            is_active: campaign.is_active,
            start_date: campaign.start_date ? toLocalDatetime(campaign.start_date) : '',
            end_date: campaign.end_date ? toLocalDatetime(campaign.end_date) : '',
            priority: campaign.priority,
            max_impressions_per_user: campaign.max_impressions_per_user,
        });
        setIsDialogOpen(true);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.title.trim()) return;
        upsertMutation.mutate({ id: editingCampaign?.id, form });
    };

    // ─── Render ─────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                        <Megaphone className="text-primary" size={28} />
                        Campañas Popup
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Gestiona popups promocionales que se muestran al abrir la app
                    </p>
                </div>
                <button
                    onClick={openCreateDialog}
                    className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors"
                >
                    <Plus size={16} />
                    Nueva Campaña
                </button>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard icon={<Megaphone size={20} />} label="Total Campañas" value={stats.total} color="text-blue-400" />
                <MetricCard icon={<ToggleRight size={20} />} label="Activas" value={stats.active} color="text-emerald-400" />
                <MetricCard icon={<Eye size={20} />} label="Impresiones" value={stats.totalImpressions} color="text-violet-400" />
                <MetricCard icon={<MousePointerClick size={20} />} label="CTR" value={`${stats.ctr}%`} color="text-amber-400" />
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="flex items-center justify-center py-16">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
            ) : campaigns.length === 0 ? (
                <div className="text-center py-16 bg-card border border-border rounded-xl">
                    <Megaphone className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">Sin campañas</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                        Crea tu primera campaña popup para empezar
                    </p>
                    <button
                        onClick={openCreateDialog}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                    >
                        <Plus size={14} className="inline mr-1" />
                        Crear Campaña
                    </button>
                </div>
            ) : (
                <div className="bg-card border border-border rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-border bg-muted/30">
                                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider p-3">Campaña</th>
                                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider p-3">Estado</th>
                                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider p-3">Plataforma</th>
                                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider p-3">
                                        <span className="flex items-center gap-1"><ArrowUpDown size={12} /> Prioridad</span>
                                    </th>
                                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider p-3">Impresiones</th>
                                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider p-3">Clics CTA</th>
                                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider p-3">CTR</th>
                                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider p-3">Fechas</th>
                                    <th className="text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider p-3">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.map((campaign) => {
                                    const status = getStatusInfo(campaign);
                                    const themeOption = THEME_OPTIONS.find(t => t.value === campaign.theme);
                                    const platformOption = PLATFORM_OPTIONS.find(p => p.value === campaign.target_platform);
                                    const impressions = campaign.stats?.impressions || 0;
                                    const clicks = campaign.stats?.cta_clicks || 0;
                                    const ctr = impressions > 0 ? ((clicks / impressions) * 100).toFixed(1) : '—';

                                    return (
                                        <tr key={campaign.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                                            {/* Campaign info */}
                                            <td className="p-3">
                                                <div className="flex items-center gap-3">
                                                    {campaign.image_url ? (
                                                        <img
                                                            src={campaign.image_url}
                                                            alt=""
                                                            className="w-10 h-10 rounded-lg object-cover border border-border"
                                                        />
                                                    ) : (
                                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${themeOption?.color || 'bg-muted'}`}>
                                                            {themeOption?.icon || <Megaphone size={16} />}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="text-sm font-medium text-foreground">{campaign.title}</p>
                                                        {campaign.subtitle && (
                                                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">{campaign.subtitle}</p>
                                                        )}
                                                        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full mt-0.5 ${themeOption?.color || ''}`}>
                                                            {themeOption?.icon}
                                                            {themeOption?.label}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Status */}
                                            <td className="p-3">
                                                <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${status.class}`}>
                                                    {status.label}
                                                </span>
                                            </td>

                                            {/* Platform */}
                                            <td className="p-3">
                                                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                                    {platformOption?.icon}
                                                    {platformOption?.label}
                                                </span>
                                            </td>

                                            {/* Priority */}
                                            <td className="p-3">
                                                <span className="text-sm font-mono text-muted-foreground">{campaign.priority}</span>
                                            </td>

                                            {/* Impressions */}
                                            <td className="p-3">
                                                <span className="text-sm text-foreground">{impressions.toLocaleString()}</span>
                                                {campaign.stats?.unique_users > 0 && (
                                                    <span className="text-[10px] text-muted-foreground ml-1">
                                                        ({campaign.stats.unique_users} <Users size={10} className="inline" />)
                                                    </span>
                                                )}
                                            </td>

                                            {/* CTA Clicks */}
                                            <td className="p-3">
                                                <span className="text-sm text-foreground">{clicks.toLocaleString()}</span>
                                            </td>

                                            {/* CTR */}
                                            <td className="p-3">
                                                <span className={`text-sm font-medium ${ctr !== '—' && parseFloat(ctr) > 5 ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                                                    {ctr}{ctr !== '—' ? '%' : ''}
                                                </span>
                                            </td>

                                            {/* Dates */}
                                            <td className="p-3">
                                                <div className="flex flex-col text-xs text-muted-foreground">
                                                    <span className="flex items-center gap-1">
                                                        <Calendar size={10} /> {formatDate(campaign.start_date)}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        → {formatDate(campaign.end_date)}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Actions */}
                                            <td className="p-3">
                                                <div className="flex items-center justify-end gap-1">
                                                    {/* Toggle */}
                                                    <button
                                                        onClick={() => toggleMutation.mutate({ id: campaign.id, is_active: !campaign.is_active })}
                                                        className={`p-1.5 rounded-md transition-colors ${campaign.is_active
                                                            ? 'text-emerald-400 hover:bg-emerald-500/20'
                                                            : 'text-muted-foreground hover:bg-muted'
                                                            }`}
                                                        title={campaign.is_active ? 'Desactivar' : 'Activar'}
                                                    >
                                                        {campaign.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                                                    </button>

                                                    {/* Edit */}
                                                    <button
                                                        onClick={() => openEditDialog(campaign)}
                                                        className="p-1.5 rounded-md text-muted-foreground hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                                                        title="Editar"
                                                    >
                                                        <Pencil size={14} />
                                                    </button>

                                                    {/* Delete */}
                                                    {deleteConfirmId === campaign.id ? (
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={() => deleteMutation.mutate(campaign.id)}
                                                                className="px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded-md hover:bg-red-500/30 transition-colors"
                                                                disabled={deleteMutation.isPending}
                                                            >
                                                                {deleteMutation.isPending ? '...' : 'Sí'}
                                                            </button>
                                                            <button
                                                                onClick={() => setDeleteConfirmId(null)}
                                                                className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded-md hover:bg-muted/80 transition-colors"
                                                            >
                                                                No
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => setDeleteConfirmId(campaign.id)}
                                                            className="p-1.5 rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                                            title="Eliminar"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Create/Edit Dialog */}
            {isDialogOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setIsDialogOpen(false)}>
                    <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
                            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                                <Megaphone size={20} className="text-primary" />
                                {editingCampaign ? 'Editar Campaña' : 'Nueva Campaña'}
                            </h2>
                            <button onClick={() => setIsDialogOpen(false)} className="text-muted-foreground hover:text-foreground">
                                <XCircle size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-4 space-y-4">
                            {/* Title */}
                            <div>
                                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Título *</label>
                                <input
                                    type="text"
                                    required
                                    value={form.title}
                                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="Ej: ¡Novedad! Nueva temporada"
                                />
                            </div>

                            {/* Subtitle */}
                            <div>
                                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Subtítulo</label>
                                <input
                                    type="text"
                                    value={form.subtitle}
                                    onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="Ej: Descubre las novedades"
                                />
                            </div>

                            {/* Body */}
                            <div>
                                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Descripción</label>
                                <textarea
                                    value={form.body}
                                    onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                                    rows={3}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="Texto descriptivo del popup..."
                                />
                            </div>

                            {/* Image Picker */}
                            <div>
                                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                    <ImageIcon size={12} className="inline mr-1" /> Imagen del popup
                                </label>

                                {/* Current image preview */}
                                {form.image_url && (
                                    <div className="relative mb-3 rounded-lg overflow-hidden border border-border">
                                        <img src={form.image_url} alt="Preview" className="w-full max-h-40 object-cover" />
                                        <button
                                            type="button"
                                            onClick={() => setForm(f => ({ ...f, image_url: '' }))}
                                            className="absolute top-2 right-2 w-6 h-6 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-black/80 transition-colors"
                                        >
                                            <X size={12} />
                                        </button>
                                        <div className="absolute bottom-0 inset-x-0 bg-black/50 backdrop-blur-sm px-2 py-1">
                                            <p className="text-[10px] text-white/70 truncate">{form.image_url}</p>
                                        </div>
                                    </div>
                                )}

                                {/* Tabs */}
                                <div className="flex gap-1 mb-3 bg-muted/30 rounded-lg p-1">
                                    <button type="button" onClick={() => setImagePickerTab('search')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${imagePickerTab === 'search' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                                        <Fish size={12} /> Buscar capturas
                                    </button>
                                    <button type="button" onClick={() => setImagePickerTab('upload')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${imagePickerTab === 'upload' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                                        <Upload size={12} /> Subir archivo
                                    </button>
                                    <button type="button" onClick={() => setImagePickerTab('url')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${imagePickerTab === 'url' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                                        <Link size={12} /> Pegar URL
                                    </button>
                                </div>

                                {/* Tab: Search captures */}
                                {imagePickerTab === 'search' && (
                                    <CaptureImageSearch
                                        search={captureSearch}
                                        onSearchChange={setCaptureSearch}
                                        onSelect={(imageUrl: string) => setForm(f => ({ ...f, image_url: imageUrl }))}
                                    />
                                )}

                                {/* Tab: Upload */}
                                {imagePickerTab === 'upload' && (
                                    <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                                        <Upload className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                                        <p className="text-sm text-muted-foreground mb-3">Arrastra una imagen o haz clic para seleccionar</p>
                                        <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${isUploading ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}>
                                            <Upload size={14} />
                                            {isUploading ? 'Subiendo...' : 'Seleccionar imagen'}
                                            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={isUploading} />
                                        </label>
                                    </div>
                                )}

                                {/* Tab: Manual URL */}
                                {imagePickerTab === 'url' && (
                                    <div className="space-y-2">
                                        <div className="flex gap-2">
                                            <input
                                                type="url"
                                                value={manualUrl}
                                                onChange={e => setManualUrl(e.target.value)}
                                                className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                                placeholder="https://ejemplo.com/imagen.jpg"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => { if (manualUrl.trim()) { setForm(f => ({ ...f, image_url: manualUrl.trim() })); setManualUrl(''); } }}
                                                className="px-3 py-2 bg-primary/10 text-primary rounded-lg text-sm font-medium hover:bg-primary/20 transition-colors"
                                            >
                                                Aplicar
                                            </button>
                                        </div>
                                        <p className="text-[10px] text-muted-foreground">Pega una URL directa a una imagen (JPG, PNG, WebP)</p>
                                    </div>
                                )}
                            </div>

                            {/* CTA URL + Text */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                                        <ExternalLink size={12} className="inline mr-1" /> URL del CTA
                                    </label>
                                    <input
                                        type="url"
                                        value={form.cta_url}
                                        onChange={e => setForm(f => ({ ...f, cta_url: e.target.value }))}
                                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        placeholder="https://..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Texto del botón</label>
                                    <input
                                        type="text"
                                        value={form.cta_text}
                                        onChange={e => setForm(f => ({ ...f, cta_text: e.target.value }))}
                                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        placeholder="Ver más"
                                    />
                                </div>
                            </div>

                            {/* Dismiss text */}
                            <div>
                                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Texto de cierre</label>
                                <input
                                    type="text"
                                    value={form.dismiss_text}
                                    onChange={e => setForm(f => ({ ...f, dismiss_text: e.target.value }))}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="Cerrar"
                                />
                            </div>

                            {/* Theme + Platform row */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                                        <Sparkles size={12} className="inline mr-1" /> Tema visual
                                    </label>
                                    <select
                                        value={form.theme}
                                        onChange={e => setForm(f => ({ ...f, theme: e.target.value as CampaignPopup['theme'] }))}
                                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    >
                                        {THEME_OPTIONS.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                                        <Smartphone size={12} className="inline mr-1" /> Plataforma
                                    </label>
                                    <select
                                        value={form.target_platform}
                                        onChange={e => setForm(f => ({ ...f, target_platform: e.target.value as CampaignPopup['target_platform'] }))}
                                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    >
                                        {PLATFORM_OPTIONS.map(p => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Priority + Max impressions */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                                        <ArrowUpDown size={12} className="inline mr-1" /> Prioridad
                                    </label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={form.priority}
                                        onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 0 }))}
                                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                    <p className="text-[10px] text-muted-foreground mt-0.5">Mayor número = mayor prioridad</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                                        <Eye size={12} className="inline mr-1" /> Máx. veces por usuario
                                    </label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={100}
                                        value={form.max_impressions_per_user}
                                        onChange={e => setForm(f => ({ ...f, max_impressions_per_user: parseInt(e.target.value) || 1 }))}
                                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </div>
                            </div>

                            {/* Schedule dates */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                                        <Calendar size={12} className="inline mr-1" /> Fecha inicio
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={form.start_date}
                                        onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                    <p className="text-[10px] text-muted-foreground mt-0.5">Vacío = inmediato</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                                        <Calendar size={12} className="inline mr-1" /> Fecha fin
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={form.end_date}
                                        onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                    <p className="text-[10px] text-muted-foreground mt-0.5">Vacío = indefinido</p>
                                </div>
                            </div>

                            {/* Active toggle */}
                            <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3">
                                <div>
                                    <p className="text-sm font-medium text-foreground">Activar inmediatamente</p>
                                    <p className="text-xs text-muted-foreground">Al activar, los usuarios verán este popup</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.is_active ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>

                            {/* Submit */}
                            <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
                                <button
                                    type="button"
                                    onClick={() => setIsDialogOpen(false)}
                                    className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={upsertMutation.isPending || !form.title.trim()}
                                    className="px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                                >
                                    {upsertMutation.isPending ? 'Guardando...' : editingCampaign ? 'Actualizar' : 'Crear Campaña'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <AdminConfirmDialog
                open={!!uploadAlert}
                intent="alert"
                title="Subida de imagen"
                description={uploadAlert}
                onCancel={() => setUploadAlert(null)}
                onConfirm={() => setUploadAlert(null)}
            />
        </div>
    );
}

// ─── Metric Card ────────────────────────────────────────────────────────────

function MetricCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string | number; color: string }) {
    return (
        <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center ${color}`}>
                    {icon}
                </div>
                <div>
                    <p className="text-xs text-muted-foreground font-medium">{label}</p>
                    <p className="text-xl font-bold text-foreground">{value}</p>
                </div>
            </div>
        </div>
    );
}

// ─── Capture Image Search ───────────────────────────────────────────────────

interface CaptureSearchResult {
    capture_id: string;
    species: string;
    display_name: string;
    image_url: string;
    created_at: string;
}

function CaptureImageSearch({
    search,
    onSearchChange,
    onSelect,
}: {
    search: string;
    onSearchChange: (v: string) => void;
    onSelect: (imageUrl: string) => void;
}) {
    const [debouncedSearch, setDebouncedSearch] = useState(search);

    // Debounce search input
    const handleSearchChange = useCallback((value: string) => {
        onSearchChange(value);
        const timer = setTimeout(() => setDebouncedSearch(value), 400);
        return () => clearTimeout(timer);
    }, [onSearchChange]);

    const { data: results = [], isLoading } = useQuery<CaptureSearchResult[]>({
        queryKey: ['capture-images-search', debouncedSearch],
        queryFn: async () => {
            const result = await apiClient.rpc<CaptureSearchResult[]>('admin_search_capture_images', {
                p_search: debouncedSearch,
                p_limit: 20,
            });
            if (result.error) throw new Error(result.error.message);
            return result.data || [];
        },
    });

    return (
        <div className="space-y-3">
            {/* Search input */}
            <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                    type="text"
                    value={search}
                    onChange={e => handleSearchChange(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="Buscar por especie o usuario..."
                />
            </div>

            {/* Results grid */}
            {isLoading ? (
                <div className="flex items-center justify-center py-8">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
            ) : results.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                    <Fish className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    {debouncedSearch ? 'Sin resultados para esta búsqueda' : 'Las últimas capturas aparecerán aquí'}
                </div>
            ) : (
                <div className="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1">
                    {results.map((item) => {
                        const fullImageUrl = item.image_url.startsWith('http')
                            ? item.image_url
                            : `${CDN_BASE_URL}/${item.image_url}`;

                        return (
                            <button
                                key={item.capture_id}
                                type="button"
                                onClick={() => onSelect(fullImageUrl)}
                                className="group relative aspect-square rounded-lg overflow-hidden border border-border hover:border-primary hover:ring-2 hover:ring-primary/30 transition-all cursor-pointer"
                            >
                                <img
                                    src={fullImageUrl}
                                    alt={item.species}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                    loading="lazy"
                                />
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-1.5 pt-4">
                                    <p className="text-[9px] text-white/90 font-medium truncate leading-tight">{item.species}</p>
                                    <p className="text-[8px] text-white/60 truncate">{item.display_name}</p>
                                </div>
                                {/* Hover overlay */}
                                <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center shadow-lg">
                                        <ImageIcon size={14} className="text-primary-foreground" />
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

